/*
 * Colour themes of the terminal: our own data file with the published
 * palettes of popular editor and terminal themes. Only themes whose palettes
 * are under a permissive licence are here; the source and licence of each
 * are listed in docs/terminal.md ("Themes"). Colour values are the palettes'
 * own; names are the themes' names, used only to identify them.
 */

/** The 16 ANSI colours in xterm's order. */
export const ANSI_KEYS = [
  "black",
  "red",
  "green",
  "yellow",
  "blue",
  "magenta",
  "cyan",
  "white",
  "brightBlack",
  "brightRed",
  "brightGreen",
  "brightYellow",
  "brightBlue",
  "brightMagenta",
  "brightCyan",
  "brightWhite",
] as const;
export type AnsiKey = (typeof ANSI_KEYS)[number];

export interface ThemeColors extends Record<AnsiKey, string> {
  background: string;
  foreground: string;
  cursor: string;
  selection: string;
}

export interface TerminalThemeSpec {
  id: string;
  /** The theme's own name (not translated). */
  name: string;
  kind: "dark" | "light";
  colors: ThemeColors;
}

/** Follows the panel's light or dark look (the `--term-*` tokens): the default. */
export const PANEL_THEME = "panel";

const ansi = (...colors: string[]): Record<AnsiKey, string> =>
  Object.fromEntries(ANSI_KEYS.map((key, i) => [key, colors[i] ?? "#000000"])) as Record<
    AnsiKey,
    string
  >;

export const THEMES: readonly TerminalThemeSpec[] = [
  {
    id: "dark-plus",
    name: "Dark+",
    kind: "dark",
    colors: {
      background: "#1e1e1e",
      foreground: "#cccccc",
      cursor: "#aeafad",
      selection: "#264f78",
      ...ansi(
        "#000000",
        "#cd3131",
        "#0dbc79",
        "#e5e510",
        "#2472c8",
        "#bc3fbc",
        "#11a8cd",
        "#e5e5e5",
        "#666666",
        "#f14c4c",
        "#23d18b",
        "#f5f543",
        "#3b8eea",
        "#d670d6",
        "#29b8db",
        "#e5e5e5",
      ),
    },
  },
  {
    id: "one-dark",
    name: "One Dark",
    kind: "dark",
    colors: {
      background: "#282c34",
      foreground: "#abb2bf",
      cursor: "#528bff",
      selection: "#3e4451",
      ...ansi(
        "#282c34",
        "#e06c75",
        "#98c379",
        "#e5c07b",
        "#61afef",
        "#c678dd",
        "#56b6c2",
        "#abb2bf",
        "#5c6370",
        "#e06c75",
        "#98c379",
        "#d19a66",
        "#61afef",
        "#c678dd",
        "#56b6c2",
        "#ffffff",
      ),
    },
  },
  {
    id: "dracula",
    name: "Dracula",
    kind: "dark",
    colors: {
      background: "#282a36",
      foreground: "#f8f8f2",
      cursor: "#f8f8f2",
      selection: "#44475a",
      ...ansi(
        "#21222c",
        "#ff5555",
        "#50fa7b",
        "#f1fa8c",
        "#bd93f9",
        "#ff79c6",
        "#8be9fd",
        "#f8f8f2",
        "#6272a4",
        "#ff6e6e",
        "#69ff94",
        "#ffffa5",
        "#d6acff",
        "#ff92df",
        "#a4ffff",
        "#ffffff",
      ),
    },
  },
  {
    id: "solarized-dark",
    name: "Solarized Dark",
    kind: "dark",
    colors: {
      background: "#002b36",
      foreground: "#839496",
      cursor: "#93a1a1",
      selection: "#073642",
      ...ansi(
        "#073642",
        "#dc322f",
        "#859900",
        "#b58900",
        "#268bd2",
        "#d33682",
        "#2aa198",
        "#eee8d5",
        "#002b36",
        "#cb4b16",
        "#586e75",
        "#657b83",
        "#839496",
        "#6c71c4",
        "#93a1a1",
        "#fdf6e3",
      ),
    },
  },
  {
    id: "nord",
    name: "Nord",
    kind: "dark",
    colors: {
      background: "#2e3440",
      foreground: "#d8dee9",
      cursor: "#d8dee9",
      selection: "#434c5e",
      ...ansi(
        "#3b4252",
        "#bf616a",
        "#a3be8c",
        "#ebcb8b",
        "#81a1c1",
        "#b48ead",
        "#88c0d0",
        "#e5e9f0",
        "#4c566a",
        "#bf616a",
        "#a3be8c",
        "#ebcb8b",
        "#81a1c1",
        "#b48ead",
        "#8fbcbb",
        "#eceff4",
      ),
    },
  },
  {
    id: "gruvbox-dark",
    name: "Gruvbox Dark",
    kind: "dark",
    colors: {
      background: "#282828",
      foreground: "#ebdbb2",
      cursor: "#ebdbb2",
      selection: "#504945",
      ...ansi(
        "#282828",
        "#cc241d",
        "#98971a",
        "#d79921",
        "#458588",
        "#b16286",
        "#689d6a",
        "#a89984",
        "#928374",
        "#fb4934",
        "#b8bb26",
        "#fabd2f",
        "#83a598",
        "#d3869b",
        "#8ec07c",
        "#ebdbb2",
      ),
    },
  },
  {
    id: "tokyo-night",
    name: "Tokyo Night",
    kind: "dark",
    colors: {
      background: "#1a1b26",
      foreground: "#c0caf5",
      cursor: "#c0caf5",
      selection: "#283457",
      ...ansi(
        "#15161e",
        "#f7768e",
        "#9ece6a",
        "#e0af68",
        "#7aa2f7",
        "#bb9af7",
        "#7dcfff",
        "#a9b1d6",
        "#414868",
        "#f7768e",
        "#9ece6a",
        "#e0af68",
        "#7aa2f7",
        "#bb9af7",
        "#7dcfff",
        "#c0caf5",
      ),
    },
  },
  {
    id: "github-dark",
    name: "GitHub Dark",
    kind: "dark",
    colors: {
      background: "#0d1117",
      foreground: "#e6edf3",
      cursor: "#2f81f7",
      selection: "#264f78",
      ...ansi(
        "#484f58",
        "#ff7b72",
        "#3fb950",
        "#d29922",
        "#58a6ff",
        "#bc8cff",
        "#39c5cf",
        "#b1bac4",
        "#6e7681",
        "#ffa198",
        "#56d364",
        "#e3b341",
        "#79c0ff",
        "#d2a8ff",
        "#56d4dd",
        "#ffffff",
      ),
    },
  },
  {
    id: "catppuccin-mocha",
    name: "Catppuccin Mocha",
    kind: "dark",
    colors: {
      background: "#1e1e2e",
      foreground: "#cdd6f4",
      cursor: "#f5e0dc",
      selection: "#585b70",
      ...ansi(
        "#45475a",
        "#f38ba8",
        "#a6e3a1",
        "#f9e2af",
        "#89b4fa",
        "#f5c2e7",
        "#94e2d5",
        "#bac2de",
        "#585b70",
        "#f38ba8",
        "#a6e3a1",
        "#f9e2af",
        "#89b4fa",
        "#f5c2e7",
        "#94e2d5",
        "#a6adc8",
      ),
    },
  },
  {
    id: "kanagawa",
    name: "Kanagawa",
    kind: "dark",
    colors: {
      background: "#1f1f28",
      foreground: "#dcd7ba",
      cursor: "#c8c093",
      selection: "#2d4f67",
      ...ansi(
        "#16161d",
        "#c34043",
        "#76946a",
        "#c0a36e",
        "#7e9cd8",
        "#957fb8",
        "#6a9589",
        "#c8c093",
        "#727169",
        "#e82424",
        "#98bb6c",
        "#e6c384",
        "#7fb4ca",
        "#938aa9",
        "#7aa89f",
        "#dcd7ba",
      ),
    },
  },
  {
    id: "light-plus",
    name: "Light+",
    kind: "light",
    colors: {
      background: "#ffffff",
      foreground: "#333333",
      cursor: "#000000",
      selection: "#add6ff",
      ...ansi(
        "#000000",
        "#cd3131",
        "#107c10",
        "#949800",
        "#0451a5",
        "#bc05bc",
        "#0598bc",
        "#555555",
        "#666666",
        "#cd3131",
        "#14ce14",
        "#b5ba00",
        "#0451a5",
        "#bc05bc",
        "#0598bc",
        "#a5a5a5",
      ),
    },
  },
  {
    id: "one-light",
    name: "One Light",
    kind: "light",
    colors: {
      background: "#fafafa",
      foreground: "#383a42",
      cursor: "#526fff",
      selection: "#e5e5e6",
      ...ansi(
        "#383a42",
        "#e45649",
        "#50a14f",
        "#c18401",
        "#4078f2",
        "#a626a4",
        "#0184bc",
        "#a0a1a7",
        "#696c77",
        "#e45649",
        "#50a14f",
        "#986801",
        "#4078f2",
        "#a626a4",
        "#0184bc",
        "#202227",
      ),
    },
  },
  {
    id: "solarized-light",
    name: "Solarized Light",
    kind: "light",
    colors: {
      background: "#fdf6e3",
      foreground: "#657b83",
      cursor: "#586e75",
      selection: "#eee8d5",
      ...ansi(
        "#073642",
        "#dc322f",
        "#859900",
        "#b58900",
        "#268bd2",
        "#d33682",
        "#2aa198",
        "#eee8d5",
        "#002b36",
        "#cb4b16",
        "#586e75",
        "#657b83",
        "#839496",
        "#6c71c4",
        "#93a1a1",
        "#fdf6e3",
      ),
    },
  },
  {
    id: "gruvbox-light",
    name: "Gruvbox Light",
    kind: "light",
    colors: {
      background: "#fbf1c7",
      foreground: "#3c3836",
      cursor: "#3c3836",
      selection: "#d5c4a1",
      ...ansi(
        "#fbf1c7",
        "#cc241d",
        "#98971a",
        "#d79921",
        "#458588",
        "#b16286",
        "#689d6a",
        "#7c6f64",
        "#928374",
        "#9d0006",
        "#79740e",
        "#b57614",
        "#076678",
        "#8f3f71",
        "#427b58",
        "#3c3836",
      ),
    },
  },
  {
    id: "github-light",
    name: "GitHub Light",
    kind: "light",
    colors: {
      background: "#ffffff",
      foreground: "#1f2328",
      cursor: "#0969da",
      selection: "#add6ff",
      ...ansi(
        "#24292f",
        "#cf222e",
        "#116329",
        "#4d2d00",
        "#0969da",
        "#8250df",
        "#1b7c83",
        "#6e7781",
        "#57606a",
        "#a40e26",
        "#1a7f37",
        "#633c01",
        "#218bff",
        "#a475f9",
        "#3192aa",
        "#8c959f",
      ),
    },
  },
  {
    id: "catppuccin-latte",
    name: "Catppuccin Latte",
    kind: "light",
    colors: {
      background: "#eff1f5",
      foreground: "#4c4f69",
      cursor: "#dc8a78",
      selection: "#acb0be",
      ...ansi(
        "#5c5f77",
        "#d20f39",
        "#40a02b",
        "#df8e1d",
        "#1e66f5",
        "#ea76cb",
        "#179299",
        "#acb0be",
        "#6c6f85",
        "#d20f39",
        "#40a02b",
        "#df8e1d",
        "#1e66f5",
        "#ea76cb",
        "#179299",
        "#bcc0cc",
      ),
    },
  },
];

export const themeById = (id: string): TerminalThemeSpec | undefined =>
  THEMES.find((theme) => theme.id === id);
